/**
 * Debug Configuration Service
 * Manages debug logging settings for the application
 */

import pb from '../lib/pocketbase';

export interface DebugConfig {
  id?: string;
  global_debug_enabled: boolean;
  partner_debug_overrides: Record<string, boolean>; // partnerId -> enabled
  recaptcha_enabled: boolean; // Toggle for reCAPTCHA verification
  created?: string;
  updated?: string;
}

class DebugConfigService {
  private config: DebugConfig | null = null;

  /**
   * Load debug configuration from PocketBase
   */
  async loadConfig(): Promise<DebugConfig> {
    try {
      // Get the first (and should be only) config record
      const records = await pb.collection('debug_config').getFullList({
        sort: '-created',
        $autoCancel: false
      });

      if (records.length > 0) {
        const record = records[0];
        this.config = {
          id: record.id,
          global_debug_enabled: record.global_debug_enabled ?? true,
          partner_debug_overrides: typeof record.partner_debug_overrides === 'string' 
            ? JSON.parse(record.partner_debug_overrides) 
            : (record.partner_debug_overrides || {}),
          recaptcha_enabled: record.recaptcha_enabled ?? true,
          created: record.created,
          updated: record.updated
        };
      } else {
        // No record yet (likely anonymous client) – use safe defaults without creating
        this.config = {
          global_debug_enabled: false,
          partner_debug_overrides: {},
          recaptcha_enabled: true
        };
      }

      return this.config;
    } catch (error) {
      console.error('Failed to load debug config:', error);
      // Fall back to logging disabled to avoid noisy consoles
      this.config = {
        global_debug_enabled: false,
        partner_debug_overrides: {},
        recaptcha_enabled: true
      };
      return this.config;
    }
  }

  /**
   * Save debug configuration to PocketBase
   */
  async saveConfig(config: DebugConfig): Promise<void> {
    try {
      const data = {
        global_debug_enabled: config.global_debug_enabled,
        partner_debug_overrides: config.partner_debug_overrides,
        recaptcha_enabled: config.recaptcha_enabled
      };

      if (config.id) {
        // Update existing
        await pb.collection('debug_config').update(config.id, data);
      } else {
        // Create new - let PocketBase auto-generate the ID
        const created = await pb.collection('debug_config').create(data);
        config.id = created.id;
      }

      this.config = config;
    } catch (error) {
      console.error('Failed to save debug config:', error);
      throw error;
    }
  }

  /**
   * Get current config (loads if not cached)
   */
  async getConfig(): Promise<DebugConfig> {
    if (!this.config) {
      await this.loadConfig();
    }
    return this.config!;
  }

  /**
   * Check if debug logging is enabled globally
   */
  async isGlobalDebugEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.global_debug_enabled;
  }

  /**
   * Check if debug logging is enabled for a specific partner
   */
  async isPartnerDebugEnabled(partnerId: string): Promise<boolean> {
    const config = await this.getConfig();
    
    // Check partner-specific override first
    if (partnerId in config.partner_debug_overrides) {
      return config.partner_debug_overrides[partnerId];
    }
    
    // Fall back to global setting
    return config.global_debug_enabled;
  }

  /**
   * Set global debug enabled state
   */
  async setGlobalDebug(enabled: boolean): Promise<void> {
    const config = await this.getConfig();
    config.global_debug_enabled = enabled;
    await this.saveConfig(config);
  }

  /**
   * Set partner-specific debug override
   */
  async setPartnerDebug(partnerId: string, enabled: boolean | null): Promise<void> {
    const config = await this.getConfig();
    
    if (enabled === null) {
      // Remove override (use global setting)
      delete config.partner_debug_overrides[partnerId];
    } else {
      config.partner_debug_overrides[partnerId] = enabled;
    }
    
    await this.saveConfig(config);
  }

  /**
   * Clear all partner overrides
   */
  async clearAllPartnerOverrides(): Promise<void> {
    const config = await this.getConfig();
    config.partner_debug_overrides = {};
    await this.saveConfig(config);
  }

  /**
   * Check if reCAPTCHA is enabled
   */
  async isRecaptchaEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.recaptcha_enabled;
  }

  /**
   * Set reCAPTCHA enabled state
   */
  async setRecaptchaEnabled(enabled: boolean): Promise<void> {
    const config = await this.getConfig();
    config.recaptcha_enabled = enabled;
    await this.saveConfig(config);
  }
}

// Singleton instance
export const debugConfigService = new DebugConfigService();

/**
 * Debug logging wrapper
 * Only logs if debug mode is enabled
 */
export const debugLog = async (message: string, ...args: any[]) => {
  try {
    const isEnabled = await debugConfigService.isGlobalDebugEnabled();
    if (isEnabled) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  } catch (error) {
    // Silently fail - don't break app if debug config fails
  }
};

/**
 * Partner-specific debug logging wrapper
 */
export const debugLogPartner = async (partnerId: string, message: string, ...args: any[]) => {
  try {
    const isEnabled = await debugConfigService.isPartnerDebugEnabled(partnerId);
    if (isEnabled) {
      console.log(`[DEBUG:${partnerId}] ${message}`, ...args);
    }
  } catch (error) {
    // Silently fail
  }
};
